import { useEffect, useState } from "react";
import { SoftwareSearchState } from "src/sections/dashboard/software/useSoftwareSearch";

// https://docs.github.com/en/graphql/overview/explorer
const GITHUB_TOKEN = process.env.REACT_APP_GITHUB_TOKEN;
const GITHUB_API_URL = "https://api.github.com/graphql";
const CACHE_NAME = "github-stats-cache"; // Define a cache name

const headers = {
  Authorization: `Bearer ${GITHUB_TOKEN}`,
  "Content-Type": "application/json",
};

type Repo = {
  owner: string;
  name: string;
  facebook?: string;
  popularAlternative?: string;
  safetyLevel?: number; // 0 = scientific research, 1= certified body approval, 2 = needs close monitoring necessary, 3=  dangerous
};

const repos: Repo[] = [
  {
    owner: "nightscout",
    name: "cgm-remote-monitor",
    facebook: "https://www.facebook.com/NightscoutProject",
    safetyLevel: 1,
  },
  {
    owner: "nightscout",
    name: "Trio",
    facebook: "https://www.facebook.com/groups/diytrio/",
    safetyLevel: 2,
  },
  {
    owner: "nightscout",
    name: "AndroidAPS",
    facebook: "https://www.facebook.com/groups/AndroidAPSUsers/",
    safetyLevel: 0,
  },
  { owner: "nightscout", name: "nightguard", safetyLevel: 2 },
  {
    owner: "NightscoutFoundation",
    name: "xDrip",
    facebook: "https://www.facebook.com/groups/xDripG5/",
    safetyLevel: 2,
  },
  {
    owner: "JohanDegraeve",
    name: "xdripswift",
    facebook: "https://www.facebook.com/groups/853994615056838",
    safetyLevel: 2,
  },
  { owner: "MarkMpn", name: "AutotuneWeb", safetyLevel: 3 },
  { owner: "openaps", name: "oref0", safetyLevel: 2 },
  {
    owner: "LoopKit",
    name: "Loop",
    facebook: "https://www.facebook.com/groups/LOOPandLEARN/",
    safetyLevel: 2,
  },
  { owner: "ivalkou", name: "freeaps", safetyLevel: 2 },
  {
    owner: "Artificial-Pancreas",
    name: "iAPS",
    facebook: "https://www.facebook.com/groups/openiaps/",
    safetyLevel: 2,
  },
  {
    owner: "piecycle",
    name: "tsunami",
    safetyLevel: 3,
  },
  {
    owner: "dtcgms1",
    name: "diabox",
    popularAlternative: "https://github.com/Bubblandevteam/diabox",
    safetyLevel: 2,
  },
  {
    owner: "creepymonster",
    name: "GlucoseDirect",
    facebook: "https://www.facebook.com/groups/4747621411996068",
    safetyLevel: 2,
  },
  {
    owner: "j-kaltes",
    name: "Juggluco",
    safetyLevel: 2,
  },
  {
    owner: "gabe565",
    name: "nightscout-menu-bar",
    safetyLevel: 2,
  },
  {
    owner: "houthacker",
    name: "nighttune",
    safetyLevel: 2,
  },
];

export interface Stats {
  id: string;
  name: string;
  url: string;
  avatarUrl: string;
  repo: string;
  owner: string;
  mainLanguage: string;
  latestRelease: string;
  releaseName: string;
  publishedAt: string;
  releaseUrl: string;
  lastCommitDate: string;
  mentionableUsersCount: number;
  device: Device;
  facebook?: string;
  safetyLevel?: number;
}

export type Device = "web" | "ios" | "android" | "web/desktop";

const fetchWithCache = async (url: string, query: string) => {
  const cache = await caches.open(CACHE_NAME);
  const cachedResponse = await cache.match(url);
  const cacheOn = false;
  if (cacheOn) {
    console.log("Returning cached response");
    return cachedResponse?.json();
  }

  const networkResponse = await fetch(url, {
    method: "POST",
    headers: headers,
    body: JSON.stringify({ query: query }),
  });

  if (networkResponse.ok) {
    const responseToCache = networkResponse.clone();
    cache.put(url, responseToCache);
    return networkResponse.json();
  } else {
    throw new Error(
      `Network response was not ok: ${networkResponse.statusText}`
    );
  }
};

const mapLanguageToDevice = (language: string): Device => {
  if (language === "Java" || language === "Kotlin") return "android";
  if (language === "Swift") return "ios";
  if (language === "JavaScript" || language === "TypeScript") return "web";
  return "web/desktop";
};

export const useGitHubStats = (searchState: SoftwareSearchState) => {
  const [stats, setStats] = useState<Stats[]>([]);
  const [loading, setLoading] = useState(true);

  const repoQueries = repos
    .map((repo) => `repo:${repo.owner}/${repo.name}`)
    .join(" ");

  const query = `
  {
    search(query: "${repoQueries}", type: REPOSITORY, first: 100) {
      nodes {
        ... on Repository {
          owner {
            avatarUrl
            login
          }
          url
          name
          releases(first: 1, orderBy: {field: CREATED_AT, direction: DESC}) {
            edges {
              node {
                tagName
                name
                publishedAt
                url
              }
            }
          }
          refs(refPrefix: "refs/heads/", first: 1, query: "master") {
            edges {
              node {
                id
                name
                target {
                  ... on Commit {
                    committedDate
                  }
                }
              }
            }
            totalCount
          }
          languages (first: 1) {
            edges {
              node {  
                name
              }
            }
          }
          mentionableUsers {
            totalCount
          }
        }
      }
    }
  }
  `;

  const fetchData = async () => {
    try {
      const data = await fetchWithCache(GITHUB_API_URL, query);

      if (data.errors) {
        console.error("Errors:", data.errors);
        return [];
      }

      const networkResponse: Stats[] = [];
      data.data.search.nodes.forEach((repo: any) => {
        const r = repos.find((r) => r.name === repo.name);
        const mainLanguage = repo?.languages?.edges[0]?.node?.name;
        const device = mapLanguageToDevice(mainLanguage);
        const stat: Stats = {
          id: repo?.refs?.edges[0]?.node?.id,
          name: repo.owner.login,
          url: r?.popularAlternative ?? repo.url,
          avatarUrl: repo.owner.avatarUrl,
          repo: repo.name,
          owner: repo.owner.login,
          mainLanguage: mainLanguage,
          latestRelease: repo?.releases?.edges[0]?.node?.tagName,
          releaseName: repo?.releases?.edges[0]?.node?.name,
          publishedAt: repo?.releases?.edges[0]?.node?.publishedAt,
          releaseUrl: repo?.releases?.edges[0]?.node?.url,
          lastCommitDate: repo?.refs?.edges[0]?.node?.target.committedDate,
          mentionableUsersCount: repo?.mentionableUsers?.totalCount,
          device: device,
          facebook: r?.facebook,
          safetyLevel: r?.safetyLevel,
        };
        networkResponse.push(stat);
      });
      setLoading(false);
      return networkResponse;
    } catch (error) {
      console.error("Error:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchData().then((networkResponse) => {
      setStats(networkResponse);
    });
  }, []);

  const filteredStats = stats.filter((stat) => {
    return searchState.filters.devices.includes(stat.device);
  });
  //  to do make dynamic
  const count = filteredStats.length;

  return { filteredStats, loading, count };
};
